import { useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "store/hooks";
import { setIsSpinnerVisible } from "store/modules/AppConfig/index.store";
import {
  handleGetWorkingAreasRequest,
  handleMassUpdateWorkingHoursRequest,
} from "store/modules/Logistics/thunk.store";
import { getCoverageAreaDealerId } from "store/modules/Logistics/logisticsCoverageArea.helpers";
import { CoverageAreaProps } from "store/modules/Logistics/types.store";
import {
  MassEditWorkingHoursFormData,
  SelectedDayInfo,
  WEEK_DAYS,
  WeekDayKey,
} from "../types";
import { INITIAL_VALUES } from "../constants";

export const useMassEditForm = (
  massEditData: CoverageAreaProps[],
  onClose: () => void,
) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const form = useForm<MassEditWorkingHoursFormData>({
    defaultValues: INITIAL_VALUES,
  });

  const { watch, setValue, handleSubmit, reset } = form;
  const watchedValues = watch();

  const hasChanges = useMemo(() => {
    return (
      Object.keys(WEEK_DAYS).some((day) => {
        const dayKey = day as WeekDayKey;
        const current = watchedValues.days[dayKey];
        const initial = INITIAL_VALUES.days[dayKey];
        return (
          current.enabled !== initial.enabled ||
          current.isOpen !== initial.isOpen ||
          current.open !== initial.open ||
          current.close !== initial.close
        );
      }) ||
      watchedValues.holiday.enabled !== INITIAL_VALUES.holiday.enabled ||
      watchedValues.holiday.isOpen !== INITIAL_VALUES.holiday.isOpen ||
      watchedValues.holiday.open !== INITIAL_VALUES.holiday.open ||
      watchedValues.holiday.close !== INITIAL_VALUES.holiday.close
    );
  }, [watchedValues]);

  const hasAnyDaySelected = useMemo(() => {
    const hasWeekDaySelected = Object.keys(WEEK_DAYS).some(
      (day) => watchedValues.days[day as WeekDayKey].enabled,
    );
    return hasWeekDaySelected || watchedValues.holiday.enabled;
  }, [watchedValues]);

  const validationError = useMemo(() => {
    const daysWithMissingTime: string[] = [];

    Object.keys(WEEK_DAYS).forEach((day) => {
      const dayKey = day as WeekDayKey;
      const dayData = watchedValues.days[dayKey];

      if (
        dayData.enabled &&
        dayData.isOpen &&
        (!dayData.open || !dayData.close)
      ) {
        daysWithMissingTime.push(t(WEEK_DAYS[dayKey].toLowerCase()));
      }
    });

    if (watchedValues.holiday.enabled && watchedValues.holiday.isOpen) {
      if (!watchedValues.holiday.open || !watchedValues.holiday.close) {
        daysWithMissingTime.push("Feriado");
      }
    }

    if (daysWithMissingTime.length > 0) {
      return "Verifique se os horários estão definidos corretamente";
    }

    return null;
  }, [watchedValues, t]);

  const selectedDays = useMemo((): SelectedDayInfo[] => {
    const result: SelectedDayInfo[] = [];

    Object.keys(WEEK_DAYS).forEach((day) => {
      const dayKey = day as WeekDayKey;
      const dayData = watchedValues.days[dayKey];

      if (dayData.enabled) {
        result.push({
          day,
          dayName: WEEK_DAYS[dayKey],
          isOpen: dayData.isOpen,
          open: dayData.open,
          close: dayData.close,
        });
      }
    });

    if (watchedValues.holiday.enabled) {
      result.push({
        day: "Holiday",
        dayName: "Feriado",
        isOpen: watchedValues.holiday.isOpen,
        open: watchedValues.holiday.open,
        close: watchedValues.holiday.close,
      });
    }

    return result;
  }, [watchedValues]);

  const handleDayEnabledChange = useCallback(
    (dayKey: WeekDayKey, enabled: boolean) => {
      if (!enabled) {
        setValue(`days.${dayKey}.isOpen`, false, { shouldDirty: false });
        setValue(`days.${dayKey}.open`, "", { shouldDirty: false });
        setValue(`days.${dayKey}.close`, "", { shouldDirty: false });
      }
    },
    [setValue],
  );

  const handleDayIsOpenChange = useCallback(
    (dayKey: WeekDayKey, isOpen: boolean) => {
      if (!isOpen) {
        setValue(`days.${dayKey}.open`, "", { shouldDirty: false });
        setValue(`days.${dayKey}.close`, "", { shouldDirty: false });
      }
    },
    [setValue],
  );

  const handleHolidayEnabledChange = useCallback(
    (enabled: boolean) => {
      if (!enabled) {
        setValue("holiday.isOpen", false, { shouldDirty: false });
        setValue("holiday.open", "", { shouldDirty: false });
        setValue("holiday.close", "", { shouldDirty: false });
      }
    },
    [setValue],
  );

  const handleHolidayIsOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        setValue("holiday.open", "", { shouldDirty: false });
        setValue("holiday.close", "", { shouldDirty: false });
      }
    },
    [setValue],
  );

  const onSubmit = useCallback(
    async (data: MassEditWorkingHoursFormData) => {
      const openingHours = Object.keys(WEEK_DAYS)
        .filter((day) => data.days[day as WeekDayKey].enabled)
        .map((day) => {
          const dayKey = day as WeekDayKey;
          const dayData = data.days[dayKey];
          return {
            day: WEEK_DAYS[dayKey],
            open: dayData.open,
            close: dayData.close,
            enabled: dayData.isOpen,
          };
        });

      const holidayOpeningHours = data.holiday.enabled
        ? [
            {
              day: "Holiday",
              open: data.holiday.open,
              close: data.holiday.close,
              enabled: data.holiday.isOpen,
            },
          ]
        : [];

      const coverageAreas = massEditData.map((coverageArea) => ({
        coverageAreaId: coverageArea.id,
        openingHours,
        ...(holidayOpeningHours.length > 0 && { holidayOpeningHours }),
      }));

      const payload = {
        dealerId: getCoverageAreaDealerId(massEditData[0]),
        coverageAreas,
      };

      dispatch(setIsSpinnerVisible(true));

      try {
        await dispatch(handleMassUpdateWorkingHoursRequest(payload)).unwrap();
        await dispatch(
          handleGetWorkingAreasRequest({
            dealerId: getCoverageAreaDealerId(massEditData[0]),
          }),
        ).unwrap();

        toast("Horários atualizados com sucesso!", {
          type: "success",
          autoClose: 3000,
        });

        onClose();
      } catch (error: any) {
        toast(
          error?.response?.data?.message ||
            "Falha ao atualizar horários. Tente novamente.",
          { type: "error", autoClose: false },
        );
      } finally {
        dispatch(setIsSpinnerVisible(false));
      }
    },
    [massEditData, dispatch, onClose],
  );

  const resetForm = useCallback(() => {
    reset(INITIAL_VALUES);
  }, [reset]);

  return {
    form,
    watchedValues,
    hasChanges,
    hasAnyDaySelected,
    validationError,
    selectedDays,
    handleSubmit,
    onSubmit,
    resetForm,
    handleDayEnabledChange,
    handleDayIsOpenChange,
    handleHolidayEnabledChange,
    handleHolidayIsOpenChange,
  };
};
